import {
  IAppointment,
  IBasePatient,
  IBrand,
  IBrandSettings,
  IPatient,
  IPatientRecall,
  IRecallSettings,
  ITreatmentStep,
  NextRecallDue,
  PatientRelationshipType,
  PatientStatus,
  RECALL_TRACK_LABELS,
  RecallTrack,
} from '@principle-theorem/principle-core/interfaces';
import {
  asReffable,
  DocumentReference,
  Firestore,
  isSameRef,
  Query,
  resolveWithConcurrency,
  snapshot,
  sortTimestampAsc,
  Timestamp,
  toMoment,
  toQuery,
  toTimestamp,
  where,
  WithRef,
} from '@principle-theorem/shared';
import { compact, first } from 'lodash';
import * as moment from 'moment-timezone';
import { Appointment } from '../appointment/appointment';
import { Brand } from '../brand';
import { Patient } from './patient';

export class Recalls {
  static initPatientRecall(settings: IRecallSettings): IPatientRecall {
    return {
      isDisabled: false,
      periodInMonths: settings.periodInMonths,
      periodIsExplicit: false,
      nextRecallDue: {
        isSet: false,
        isExplicit: false,
        dueAt: undefined,
      },
    };
  }

  static getTimeDistance(
    timestamp?: Timestamp,
    now: moment.Moment = moment()
  ): string | undefined {
    if (!timestamp) {
      return undefined;
    }
    const compareDate = toMoment(timestamp);
    const isFuture = compareDate.isAfter(now);
    const duration = moment.duration({
      from: isFuture ? now : compareDate,
      to: isFuture ? compareDate : now,
    });
    const months = Math.floor(duration.asMonths());
    const unit = months ? 'month' : 'day';
    const value = months ? months : duration.days();
    const timeDistance = `${value} ${unit}${value > 1 ? 's' : ''}`;
    return isFuture ? `in ${timeDistance}` : `${timeDistance} ago`;
  }

  static async buildPatientRecall(
    patient: WithRef<IPatient>,
    settings: IRecallSettings,
    track: RecallTrack,
    recallUpdates?: IPatientRecall,
    refreshLastRecallAppointment: boolean = false,
    forceNextDueDate: boolean = false
  ): Promise<IPatientRecall> {
    const recall =
      recallUpdates ??
      patient[Recalls.trackToField(track)] ??
      Recalls.initPatientRecall(settings);
    if (recall.isDisabled) {
      return recall;
    }

    const recallAppointments = await Recalls.getPatientRecallAppointments(
      patient,
      settings
    );

    const lastRecallAppointment =
      await Recalls.resolvePatientLastRecallAppointment(
        patient,
        recallAppointments,
        track,
        refreshLastRecallAppointment
      );

    const lastCompletedAt = lastRecallAppointment
      ? lastRecallAppointment?.event?.from
      : !refreshLastRecallAppointment
        ? recallUpdates?.lastCompletedAt
        : undefined;

    const periodInMonths = recall.periodInMonths ?? settings.periodInMonths;

    const updatedNextDue =
      lastCompletedAt && periodInMonths
        ? toMoment(lastCompletedAt).add(periodInMonths, 'months')
        : moment();
    const nextDue: NextRecallDue =
      forceNextDueDate || !recall.nextRecallDue.isExplicit
        ? {
            isSet: true,
            isExplicit: false,
            dueAt: toTimestamp(updatedNextDue),
          }
        : recall.nextRecallDue;

    const nextRecallAppointment =
      Recalls.findNextAppointment(recallAppointments);

    return {
      isDisabled: false,
      periodInMonths,
      periodIsExplicit: false,
      nextRecallDue: nextDue,
      lastCompletedAt,
      lastRecallAppointment: lastRecallAppointment?.ref,
      nextRecallAppointment: nextRecallAppointment?.ref,
    };
  }

  static enabledTracks(
    patient: IPatient,
    brandSettings: IBrandSettings
  ): RecallTrack[] {
    return [RecallTrack.Primary, RecallTrack.Secondary].filter((track) => {
      const recall = patient[Recalls.trackToField(track)];
      return (
        !!brandSettings[Recalls.trackToSettingsField(track)] &&
        !!recall &&
        !recall.isDisabled
      );
    });
  }

  static async getPatientRecallAppointments(
    patient: WithRef<IPatient>,
    settings: IRecallSettings
  ): Promise<WithRef<IAppointment>[]> {
    const appointments$ = Patient.withPatientRelationships$(
      patient,
      [PatientRelationshipType.DuplicatePatient],
      (patientReffable) =>
        Firestore.query$(Patient.appointmentCol(patientReffable))
    );
    const appointments = await snapshot(appointments$);

    const recallAppointments = await resolveWithConcurrency(
      appointments,
      5,
      async (appointment) => {
        if (appointment.deleted) {
          return;
        }
        const isRecallAppointment = await Recalls.isRecallAppointment(
          settings,
          appointment
        );
        return isRecallAppointment ? appointment : undefined;
      }
    );

    return compact(recallAppointments);
  }

  static async resolvePatientLastRecallAppointment(
    patient: WithRef<IPatient>,
    recallAppointments: WithRef<IAppointment>[],
    track: RecallTrack,
    recalculate: boolean = false
  ): Promise<WithRef<IAppointment> | undefined> {
    const patientRecall = patient[Recalls.trackToField(track)];
    if (recalculate || !patientRecall?.lastRecallAppointment) {
      return Recalls.findLastCompletedAppointment(recallAppointments);
    }
    if (!patientRecall?.lastRecallAppointment) {
      return;
    }
    return Firestore.getDoc(patientRecall.lastRecallAppointment);
  }

  static findLastCompletedAppointment(
    recallAppointments: WithRef<IAppointment>[],
    now: moment.Moment = moment()
  ): WithRef<IAppointment> | undefined {
    const pastCompletedAppointmentsDescending = recallAppointments
      .filter((appointment) => Appointment.isComplete(appointment))
      .filter(
        (appointment) =>
          appointment.event?.from &&
          toMoment(appointment.event.from).isBefore(now)
      )
      .sort((a, b) => sortTimestampAsc(a.event?.from, b.event?.from))
      .reverse();
    return first(pastCompletedAppointmentsDescending);
  }

  static findNextAppointment(
    recallAppointments: WithRef<IAppointment>[],
    now: moment.Moment = moment()
  ): WithRef<IAppointment> | undefined {
    const futureAppointmentsAscending = recallAppointments
      .filter(
        (appointment) =>
          appointment.event?.from &&
          toMoment(appointment.event.from).isAfter(now)
      )
      .sort((a, b) => sortTimestampAsc(a.event?.from, b.event?.from));
    return first(futureAppointmentsAscending);
  }

  static async isRecallAppointment(
    settings: IRecallSettings,
    appointment: WithRef<IAppointment>
  ): Promise<boolean> {
    const step = await Appointment.treatmentStep(appointment);
    return Recalls.isRecallStep(settings, step);
  }

  static isRecallStep(
    settings: IRecallSettings,
    step?: ITreatmentStep
  ): boolean {
    const treatments = step?.treatments ?? [];

    return treatments.some((treatment) =>
      settings.treatmentRefs.some((settingTreatment) =>
        isSameRef(settingTreatment, treatment.config.ref)
      )
    );
  }

  static enabledPatientsWithoutRecallDueDateQuery(
    brandRef: DocumentReference<IBrand>,
    track: RecallTrack
  ): Query<IPatient> {
    const field = Recalls.trackToField(track);
    return toQuery(
      Brand.patientCol(asReffable(brandRef)),
      where('deleted', '==', false),
      where('status', '==', PatientStatus.Active),
      where(`${field}.isDisabled`, '==', false),
      where(`${field}.nextRecallDue.isSet`, '==', false),
      where(`${field}.nextRecallDue.isExplicit`, '==', false)
    );
  }

  static patientsWithDefaultPeriodQuery(
    brandRef: DocumentReference<IBrand>,
    track: RecallTrack
  ): Query<IPatient> {
    const field = Recalls.trackToField(track);
    return toQuery(
      Brand.patientCol(asReffable(brandRef)),
      where('deleted', '==', false),
      where('status', '==', PatientStatus.Active),
      where(`${field}.periodIsExplicit`, '==', false)
    );
  }

  static patientsWithOutdatedDefaultPeriodQuery(
    brandRef: DocumentReference<IBrand>,
    settings: IRecallSettings,
    track: RecallTrack
  ): Query<IPatient> {
    const field = Recalls.trackToField(track);
    return toQuery(
      Brand.patientCol(asReffable(brandRef)),
      where('deleted', '==', false),
      where('status', '==', PatientStatus.Active),
      where(`${field}.periodIsExplicit`, '==', false),
      where(`${field}.periodInMonths`, '!=', settings.periodInMonths)
    );
  }

  static patientsWithoutRecallDueDateQuery(
    brandRef: DocumentReference<IBrand>,
    track: RecallTrack
  ): Query<IPatient> {
    const field = Recalls.trackToField(track);
    return toQuery(
      Brand.patientCol(asReffable(brandRef)),
      where('deleted', '==', false),
      where('status', '==', PatientStatus.Active),
      where(`${field}.nextRecallDue.isSet`, '==', false),
      where(`${field}.nextRecallDue.isExplicit`, '==', false)
    );
  }

  static patientsWithAutoRecallDueDateQuery(
    brandRef: DocumentReference<IBrand>,
    track: RecallTrack
  ): Query<IPatient> {
    const field = Recalls.trackToField(track);
    return toQuery(
      Brand.patientCol(asReffable(brandRef)),
      where('deleted', '==', false),
      where('status', '==', PatientStatus.Active),
      where(`${field}.nextRecallDue.isSet`, '==', true),
      where(`${field}.nextRecallDue.isExplicit`, '==', false)
    );
  }

  static patientsWithExplicitRecallDueDateQuery(
    brandRef: DocumentReference<IBrand>,
    track: RecallTrack
  ): Query<IPatient> {
    const field = Recalls.trackToField(track);
    return toQuery(
      Brand.patientCol(asReffable(brandRef)),
      where('deleted', '==', false),
      where('status', '==', PatientStatus.Active),
      where(`${field}.nextRecallDue.isSet`, '==', true),
      where(`${field}.nextRecallDue.isExplicit`, '==', true)
    );
  }

  static trackToField(
    track: RecallTrack
  ): keyof Pick<IBasePatient, 'recall' | 'secondaryRecall'> {
    return track === RecallTrack.Primary ? 'recall' : 'secondaryRecall';
  }

  static trackToSettingsField(
    track: RecallTrack
  ): keyof Pick<IBrandSettings, 'recalls' | 'secondaryRecalls'> {
    return track === RecallTrack.Primary ? 'recalls' : 'secondaryRecalls';
  }

  static trackLabel(brand: IBrand | undefined, track: RecallTrack): string {
    const label =
      brand?.settings?.[Recalls.trackToSettingsField(track)]?.label?.trim();

    if (label) {
      return label;
    }

    return RECALL_TRACK_LABELS[track];
  }
}
